import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Brain,
  RefreshCw,
  Sparkles,
  Target,
  BookOpen,
  MessageSquare,
  Users,
  ShieldAlert,
  Building2,
  ChevronDown,
  Info,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import supabase from '../lib/supabaseClient';
import { VISIBLE_TENANTS_OR } from '../lib/tenantVisibility';

// ----------------------------------------------------------------------------
// Constants + helpers
// ----------------------------------------------------------------------------

const STAGES = ['initial', 'followup', 'reply'];
const STAGE_LABELS = {
  initial: 'First message',
  followup: 'Follow-up',
  reply: 'Reply',
};

const OPENING_LABELS = {
  question_open: 'Question-based',
  question_yes_no: 'Yes/No question',
  hook_then_question: 'Hook & question',
  statement: 'Statement-based',
  compliment: 'Compliment-based',
  reference_situation: 'Observation-based',
  direct_ask: 'Direct ask',
};

const TONE_LABELS = {
  friendly_casual: 'Friendly Casual',
  professional_direct: 'Professional Direct',
  warm_empathetic: 'Warm Empathetic',
};

const CTA_LABELS = {
  none: 'No Call to Action',
  soft: 'Soft Call to Action',
  explicit: 'Explicit Call to Action',
};

function humanize(value) {
  if (!value || value === 'none') return value;
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function prettyOpening(v) {
  return OPENING_LABELS[v] || humanize(v);
}
function prettyTone(v) {
  return TONE_LABELS[v] || humanize(v);
}
function prettyCta(v) {
  return CTA_LABELS[v] || humanize(v);
}

function rateFromArm(arm) {
  const attempts = arm.total_attempts || 0;
  const wins = arm.hot_conversions || 0;
  if (attempts === 0) return { attempts, wins, rate: null };
  return { attempts, wins, rate: wins / attempts };
}

function groupArmsBy(arms, key) {
  const groups = new Map();
  for (const arm of arms) {
    const v = arm[key];
    if (!v || v === 'none') continue;
    const cur = groups.get(v) || { value: v, attempts: 0, wins: 0, armCount: 0 };
    cur.attempts += arm.total_attempts || 0;
    cur.wins += arm.hot_conversions || 0;
    cur.armCount += 1;
    groups.set(v, cur);
  }
  return Array.from(groups.values()).map((g) => ({
    ...g,
    rate: g.attempts > 0 ? g.wins / g.attempts : null,
  }));
}

function archetypeFromBucket(bucket) {
  if (!bucket || bucket === 'any') return null;
  const parts = bucket.split('_');
  if (parts.length !== 3) return null;
  const [d, s] = parts;
  if (s === 'high') {
    return d === 'high'
      ? { key: 'analytical', name: 'Analytical Decision Makers', tag: 'Data-Backed', dot: 'bg-blue-500', tagBg: 'bg-blue-100 text-blue-700' }
      : { key: 'skeptic', name: 'Data-Driven Skeptics', tag: 'Proof & Evidence', dot: 'bg-pink-500', tagBg: 'bg-pink-100 text-pink-700' };
  }
  if (d === 'high') {
    return { key: 'quick', name: 'Quick Decision Makers', tag: 'Direct & Urgent', dot: 'bg-purple-500', tagBg: 'bg-purple-100 text-purple-700' };
  }
  return { key: 'relationship', name: 'Relationship Builders', tag: 'Relationship First', dot: 'bg-indigo-500', tagBg: 'bg-indigo-100 text-indigo-700' };
}

function weeksSince(iso) {
  if (!iso) return 0;
  const diff = Date.now() - new Date(iso).getTime();
  return Math.max(1, Math.ceil(diff / (7 * 86_400_000)));
}

const MIN_SAMPLE_FOR_RATE = 5;

function fmtPct(rate, digits = 0) {
  if (rate == null) return '—';
  return `${(rate * 100).toFixed(digits)}%`;
}

function rateDisplay(group, digits = 0) {
  if (!group || group.attempts < MIN_SAMPLE_FOR_RATE) return '—';
  return fmtPct(group.rate, digits);
}

function hasMeasurableRate(group) {
  return group && group.attempts >= MIN_SAMPLE_FOR_RATE;
}

function fmtInt(n) {
  return new Intl.NumberFormat().format(n || 0);
}

// ----------------------------------------------------------------------------
// Admin tenant dropdown (mirrored from ABTestingDashboard)
// ----------------------------------------------------------------------------

const AdminTenantFilterDropdown = ({ value, options, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedOption = options.find((opt) => opt.value === value);
  return (
    <div className="relative inline-block">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-4 py-2 bg-white border border-indigo-200 rounded-lg text-sm font-medium text-indigo-700 cursor-pointer transition-all hover:bg-indigo-50 hover:border-indigo-300 min-w-[220px]"
      >
        <Building2 size={16} className="text-indigo-500" />
        <span className="flex-1 text-left truncate">{selectedOption?.label || 'Select Tenant'}</span>
        <ChevronDown size={16} className={`text-indigo-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto p-1">
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() => { onChange(option.value); setIsOpen(false); }}
                className={`block w-full px-3 py-2.5 text-left rounded-md text-sm transition-colors ${
                  value === option.value ? 'bg-indigo-50 text-indigo-600 font-semibold' : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Section components
// ----------------------------------------------------------------------------

function HeroHeader({ rightSlot }) {
  return (
    <div className="bg-gradient-to-r from-indigo-50 via-purple-50 to-indigo-50 border border-indigo-100 rounded-xl p-5 flex items-center justify-between gap-4">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-sm">
          <Brain className="text-white" size={22} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-indigo-900">AI Learning Intelligence</h2>
          <p className="text-sm text-indigo-600">Your AI learns from every conversation to improve results</p>
        </div>
      </div>
      <div>{rightSlot}</div>
    </div>
  );
}

function KeyDiscoveryCard({ openingGroups, totalDecisions }) {
  const eligible = openingGroups.filter((g) => g.attempts >= 5 && g.rate != null);
  if (eligible.length < 2) {
    return (
      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center shrink-0">
            <Sparkles className="text-white" size={18} />
          </div>
          <div>
            <h3 className="text-base font-bold text-emerald-900">Key Discovery</h3>
            <p className="text-emerald-800 mt-2 text-sm leading-relaxed">
              Your AI is still gathering data. Once a handful of conversations convert to hot leads,
              you'll see the patterns it's learning here, like which opening style wins most often.
            </p>
            <p className="text-emerald-600 text-xs mt-2">
              {fmtInt(totalDecisions)} messages analyzed so far.
            </p>
          </div>
        </div>
      </div>
    );
  }
  const sorted = [...eligible].sort((a, b) => b.rate - a.rate);
  const top = sorted[0];
  const bottom = sorted[sorted.length - 1];
  const points = Math.round((top.rate - bottom.rate) * 100);
  return (
    <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center shrink-0">
          <Sparkles className="text-white" size={18} />
        </div>
        <div>
          <h3 className="text-base font-bold text-emerald-900">Key Discovery</h3>
          <p className="text-emerald-800 mt-2 text-sm">
            <span className="font-semibold text-emerald-700">{prettyOpening(top.value)}</span> outperform{' '}
            <span className="font-semibold text-rose-700">{prettyOpening(bottom.value)}</span> by{' '}
            <span className="font-bold">{points} points</span>
          </p>
          <p className="text-emerald-600 text-xs mt-2">
            Your AI now prioritizes {prettyOpening(top.value).toLowerCase()} in opening messages
            based on {fmtInt(totalDecisions)} analyzed conversations.
          </p>
        </div>
      </div>
    </div>
  );
}

function WhatsConvertingCard({ overallRate, totalAttempts, totalWins, toneGroups, openingGroups, ctaGroups }) {
  const hasData = totalAttempts > 0 && totalWins > 0;
  return (
    <div className="bg-amber-50 border border-amber-100 rounded-xl p-5">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center shrink-0">
            <Target className="text-white" size={18} />
          </div>
          <div>
            <h3 className="text-base font-bold text-amber-900">What's Actually Converting?</h3>
            <p className="text-amber-700 text-sm">
              Real conversion rates from cold leads to hot conversations, not just engagement, but actual results.
            </p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-3xl font-extrabold text-amber-600 leading-none">{hasData ? fmtPct(overallRate, 1) : '—'}</div>
          <div className="text-xs text-amber-500 mt-1">{hasData ? 'overall rate' : 'no conversions yet'}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ConvertingColumn title="Communication Style" groups={toneGroups} renderLabel={prettyTone} />
        <ConvertingColumn title="Opening Style" groups={openingGroups} renderLabel={prettyOpening} />
        <ConvertingColumn title="Call to Action" groups={ctaGroups} renderLabel={prettyCta} />
      </div>

      <div className="bg-amber-100/60 border border-amber-200 rounded-lg p-3 mt-4">
        <p className="text-xs text-amber-900">
          <span className="font-semibold">How this helps:</span>{' '}
          Your AI automatically favors the highest-converting approaches when messaging cold leads.
          The more conversations, the smarter it gets.
        </p>
      </div>
    </div>
  );
}

function ConvertingColumn({ title, groups, renderLabel }) {
  const measurable = groups.filter(hasMeasurableRate);
  const sorted = measurable.length > 0
    ? [...measurable].sort((a, b) => b.rate - a.rate)
    : [...groups].sort((a, b) => b.attempts - a.attempts);
  const top = sorted.slice(0, 3);
  const anyMeasurable = measurable.length > 0;

  return (
    <div className="bg-white rounded-lg border border-amber-200 p-3">
      <div className="text-sm font-bold text-gray-900 mb-2">{title}</div>
      {top.length === 0 ? (
        <div className="text-xs text-gray-400 py-2">Still gathering data.</div>
      ) : (
        <div className="space-y-1.5">
          {top.map((g, i) => {
            const measurable = hasMeasurableRate(g);
            return (
              <div key={g.value} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className={`w-1.5 h-1.5 rounded-full ${anyMeasurable && i === 0 && measurable ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                  <span className="text-gray-700 truncate">{renderLabel(g.value)}</span>
                </div>
                <span className="text-gray-600 font-medium shrink-0 ml-2">{rateDisplay(g)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LearningProgressCard({ totalDecisions, personalityPatterns, weeksActive }) {
  const target = 10_000;
  const pct = Math.min(100, (totalDecisions / target) * 100);
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-9 h-9 bg-purple-100 rounded-lg flex items-center justify-center">
          <BookOpen className="text-purple-600" size={18} />
        </div>
        <h3 className="text-base font-bold text-gray-900">Learning Progress</h3>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-1.5">
          <span className="text-gray-600">Conversations Analyzed</span>
          <span className="text-gray-900 font-semibold">
            {fmtInt(totalDecisions)} <span className="text-gray-400 font-normal">/ {fmtInt(target)}</span>
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-extrabold text-purple-600">{fmtInt(personalityPatterns)}</div>
          <div className="text-xs text-purple-700 font-medium mt-0.5">Personality Patterns</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-extrabold text-purple-600">{fmtInt(weeksActive)}</div>
          <div className="text-xs text-purple-700 font-medium mt-0.5">Weeks Active</div>
        </div>
      </div>
    </div>
  );
}

function MessageStyleEffectivenessCard({ openingGroups }) {
  const measurable = openingGroups.filter(hasMeasurableRate);
  const ranked = [...measurable].sort((a, b) => b.rate - a.rate).slice(0, 6);
  const best = ranked[0];
  const baseline = best ? best.rate : 0;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
          <MessageSquare className="text-blue-600" size={18} />
        </div>
        <h3 className="text-base font-bold text-gray-900">AI Message Style Effectiveness</h3>
      </div>
      <p className="text-sm text-gray-500 ml-12 mb-4">Which opening styles drive the most hot conversions</p>

      {ranked.length === 0 ? (
        <div className="text-sm text-gray-500 py-4 text-center">
          Your AI hasn't sent enough messages yet to compare styles.
        </div>
      ) : (
        <div className="space-y-2.5">
          {ranked.map((g, i) => {
            const isTop = i === 0;
            const widthPct = baseline > 0 ? Math.max(10, (g.rate / baseline) * 100) : 100;
            return (
              <div key={g.value}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${isTop ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                    <span className="text-gray-800 font-medium">{prettyOpening(g.value)}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    <span className="text-gray-500">{fmtInt(g.attempts)} msgs</span>
                    <span className="font-bold text-emerald-600">{rateDisplay(g, 1)}</span>
                  </div>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${isTop ? 'bg-emerald-500' : 'bg-emerald-300'}`}
                    style={{ width: `${widthPct}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 mt-4">
        <p className="text-xs text-blue-900">
          <span className="font-semibold">What this means:</span>{' '}
          Higher rates indicate better lead engagement. Your AI automatically favors higher-converting openings.
        </p>
      </div>
    </div>
  );
}

function LeadPersonalityTypesCard({ archetypeBreakdown }) {
  const total = archetypeBreakdown.reduce((s, a) => s + a.count, 0);
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-9 h-9 bg-indigo-100 rounded-lg flex items-center justify-center">
          <Users className="text-indigo-600" size={18} />
        </div>
        <h3 className="text-base font-bold text-gray-900">Lead Personality Types</h3>
      </div>
      <p className="text-sm text-gray-500 ml-12 mb-4">How your AI categorizes and adapts to different leads</p>

      {total === 0 ? (
        <div className="text-sm text-gray-500 py-4 text-center">
          Personality detection activates as your AI gathers more responses from each lead.
        </div>
      ) : (
        <>
          <div className="flex h-2 rounded-full overflow-hidden mb-4">
            {archetypeBreakdown.map((a) => (
              <div key={a.key} className={a.dot} style={{ width: `${(a.count / total) * 100}%` }} />
            ))}
          </div>
          <div className="space-y-2">
            {archetypeBreakdown.map((a) => (
              <div key={a.key} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${a.dot}`} />
                  <span className="text-gray-800 font-medium">{a.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${a.tagBg}`}>{a.tag}</span>
                  <span className="text-gray-700 font-semibold w-10 text-right">
                    {Math.round((a.count / total) * 100)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 mt-4">
        <p className="text-xs text-indigo-900">
          <span className="font-semibold">How it works:</span>{' '}
          Your AI detects each lead's personality and adjusts its communication style automatically.
        </p>
      </div>
    </div>
  );
}

function PausedApproachesCard({ quarantines }) {
  if (quarantines.length === 0) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
        <ShieldAlert size={16} className="text-rose-500" />
        <h3 className="text-sm font-semibold text-gray-900">Paused approaches</h3>
        <span className="text-xs text-gray-500">{quarantines.length} paused</span>
      </div>
      <div className="divide-y divide-gray-100">
        {quarantines.map((row) => (
          <div key={row.id} className="px-5 py-3 flex items-center justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-rose-50 text-rose-700">
                  {STAGE_LABELS[row.conversation_stage] || row.conversation_stage}
                </span>
                <span className="text-xs text-gray-700">
                  {prettyOpening(row.opening_type)} · {prettyTone(row.tone)}
                </span>
              </div>
              <div className="text-xs text-gray-500">
                {row.quarantined_reason || 'Underperforming, paused automatically'}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Main component
// ----------------------------------------------------------------------------

// Review sandbox only. A reviewer sees this page on an account that is only days old, so it has to say plainly what
// the page is, how the AI really learns, and that the numbers on it are sample data: real learning needs hundreds of
// real conversations over weeks, which a demo account cannot have. Never shown to real customers.
function ReviewerLearningExplainer() {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Info className="h-5 w-5 text-indigo-500" />
          <h3 className="text-base font-bold text-indigo-900">What you are looking at</h3>
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
          {open ? 'Hide' : 'Show'}
        </button>
      </div>
      {open && (
        <div className="mt-3 space-y-3 text-sm leading-relaxed text-indigo-900">
          <p>
            <span className="font-semibold">Sample data.</span> Real learning needs hundreds of real conversations over several weeks, and a
            demo account cannot have that. The numbers on this page are made up to show you what it looks like after a business has been running
            for a while. Nothing here is learning from your test conversations.
          </p>
          <div>
            <p className="font-semibold">How the AI really learns</p>
            <p>
              Every text SurFox sends is written a little differently: it might open with a question or a statement, sound friendly or
              professional, or ask for a call directly or gently. SurFox keeps score of which styles lead to a hot lead, meaning someone ready to
              talk to a person. Over time it sends more of what works for that business&apos;s own customers and less of what does not. Nobody has
              to set this up or tune it.
            </p>
          </div>
          <div>
            <p className="font-semibold">What each card on this page shows</p>
            <ul className="mt-1 list-disc space-y-1 pl-5">
              <li><span className="font-medium">Key Discovery:</span> the clearest winner so far, for example that opening with a question beats opening with a statement.</li>
              <li><span className="font-medium">What&apos;s Actually Converting:</span> the share of conversations that became hot leads, broken down by tone, opening style and call to action.</li>
              <li><span className="font-medium">Learning progress:</span> how many conversations the AI has studied so far.</li>
              <li><span className="font-medium">Paused approaches:</span> anything SurFox stopped using because it was not working.</li>
            </ul>
          </div>
          <p>
            On a real account these figures come only from that business&apos;s own conversations, and they update as replies come in.
          </p>
        </div>
      )}
    </div>
  );
}

export default function LearningAnalytics() {
  const { user } = useAuth();
  const isGlobalAdmin = user?.role === 'global_admin';
  const ownTenantId = user?.tenant_id;

  const [adminTenants, setAdminTenants] = useState([]);
  const [selectedAdminTenant, setSelectedAdminTenant] = useState(ownTenantId || '');
  const tenantId = isGlobalAdmin ? selectedAdminTenant : ownTenantId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [arms, setArms] = useState([]);
  const [totalDecisions, setTotalDecisions] = useState(0);
  const [firstDecisionAt, setFirstDecisionAt] = useState(null);
  const [personalityBuckets, setPersonalityBuckets] = useState([]);
  const [quarantines, setQuarantines] = useState([]);

  useEffect(() => {
    if (!isGlobalAdmin) return;
    let cancelled = false;
    (async () => {
      const { data, error: err } = await supabase
        .from('tenants')
        .select('id, name')
        .or(VISIBLE_TENANTS_OR) // hide inactive/suspended from global_admin dropdown
        .order('name');
      if (cancelled) return;
      if (err) {
        console.error('[LearningAnalytics] Error fetching tenants:', err);
        return;
      }
      setAdminTenants(data || []);
      setSelectedAdminTenant((prev) => prev || ownTenantId || (data && data[0]?.id) || '');
    })();
    return () => { cancelled = true; };
  }, [isGlobalAdmin, ownTenantId]);

  const fetchAll = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);
    try {
      const armsQuery = supabase
        .from('learning_arms')
        .select('id, conversation_stage, opening_type, tone, length_bucket, cta_style, personality_bucket, alpha, beta, hot_conversions, total_attempts, quarantined, quarantined_reason, updated_at')
        .eq('tenant_id', tenantId);

      const decisionsCountQuery = supabase
        .from('learning_decisions')
        .select('id', { count: 'exact', head: true })
        .eq('tenant_id', tenantId);

      const firstDecisionQuery = supabase
        .from('learning_decisions')
        .select('selected_at')
        .eq('tenant_id', tenantId)
        .order('selected_at', { ascending: true })
        .limit(1);

      const personalitySampleQuery = supabase
        .from('learning_decisions')
        .select('arm:learning_arms!arm_id(personality_bucket)')
        .eq('tenant_id', tenantId)
        .order('selected_at', { ascending: false })
        .limit(500);

      const [armsResult, countResult, firstResult, personalityResult] = await Promise.all([
        armsQuery,
        decisionsCountQuery,
        firstDecisionQuery,
        personalitySampleQuery,
      ]);

      if (armsResult.error) throw armsResult.error;
      if (countResult.error) throw countResult.error;
      if (firstResult.error) throw firstResult.error;
      if (personalityResult.error) throw personalityResult.error;

      setArms(armsResult.data || []);
      setTotalDecisions(countResult.count ?? 0);
      setFirstDecisionAt(firstResult.data?.[0]?.selected_at || null);
      setPersonalityBuckets(
        (personalityResult.data || [])
          .map((r) => r.arm?.personality_bucket)
          .filter(Boolean)
      );
      setQuarantines((armsResult.data || []).filter((a) => a.quarantined));
    } catch (e) {
      console.error('LearningAnalytics fetch failed:', e);
      setError(e.message || 'Failed to load AI activity');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // ----- derived aggregates -----
  const activeArms = useMemo(() => arms.filter((a) => !a.quarantined), [arms]);

  const totals = useMemo(() => {
    const totalAttempts = activeArms.reduce((s, a) => s + (a.total_attempts || 0), 0);
    const totalWins = activeArms.reduce((s, a) => s + (a.hot_conversions || 0), 0);
    const overallRate = totalAttempts > 0 ? totalWins / totalAttempts : null;
    return { totalAttempts, totalWins, overallRate };
  }, [activeArms]);

  const openingGroups = useMemo(() => groupArmsBy(activeArms, 'opening_type'), [activeArms]);
  const toneGroups = useMemo(() => groupArmsBy(activeArms, 'tone'), [activeArms]);
  const ctaGroups = useMemo(() => groupArmsBy(activeArms, 'cta_style'), [activeArms]);

  const personalityPatterns = useMemo(() => {
    const set = new Set();
    for (const a of activeArms) {
      if (a.personality_bucket && a.personality_bucket !== 'any') set.add(a.personality_bucket);
    }
    return set.size;
  }, [activeArms]);

  const archetypeBreakdown = useMemo(() => {
    const counts = new Map();
    for (const bucket of personalityBuckets) {
      const a = archetypeFromBucket(bucket);
      if (!a) continue;
      const cur = counts.get(a.key) || { ...a, count: 0 };
      cur.count += 1;
      counts.set(a.key, cur);
    }
    return Array.from(counts.values()).sort((a, b) => b.count - a.count);
  }, [personalityBuckets]);

  const weeksActive = useMemo(() => weeksSince(firstDecisionAt), [firstDecisionAt]);

  if (!tenantId) {
    return (
      <div className="p-6 text-sm text-gray-500">
        Sign in to see what your AI is learning.
      </div>
    );
  }

  const rightSlot = (
    <div className="flex items-center gap-2">
      {isGlobalAdmin && adminTenants.length > 0 && (
        <AdminTenantFilterDropdown
          value={selectedAdminTenant}
          options={adminTenants.map((t) => ({
            value: t.id,
            label: t.id === ownTenantId ? `${t.name} (you)` : t.name,
          }))}
          onChange={setSelectedAdminTenant}
        />
      )}
      <button
        onClick={fetchAll}
        disabled={loading}
        className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-200 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50"
      >
        <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        Refresh
      </button>
    </div>
  );

  return (
    <div className="p-6 space-y-4 max-w-6xl mx-auto">
      <HeroHeader rightSlot={rightSlot} />

      {user?.is_reviewer === true && <ReviewerLearningExplainer />}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">
          {error}
        </div>
      )}

      <KeyDiscoveryCard openingGroups={openingGroups} totalDecisions={totalDecisions} />

      <WhatsConvertingCard
        overallRate={totals.overallRate}
        totalAttempts={totals.totalAttempts}
        totalWins={totals.totalWins}
        toneGroups={toneGroups}
        openingGroups={openingGroups}
        ctaGroups={ctaGroups}
      />

      <LearningProgressCard
        totalDecisions={totalDecisions}
        personalityPatterns={personalityPatterns}
        weeksActive={weeksActive}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MessageStyleEffectivenessCard openingGroups={openingGroups} />
        <LeadPersonalityTypesCard archetypeBreakdown={archetypeBreakdown} />
      </div>

      <PausedApproachesCard quarantines={quarantines} />
    </div>
  );
}
